import { useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

const NORMAL_UPDATE_INTERVAL = 5000; // 5 seconds
const NAVIGATION_UPDATE_INTERVAL = 5000; // 5 seconds
const LOCATION_CHECK_INTERVAL = 5000; // Force location check every 5 seconds

const LocationManager = forwardRef(function LocationManager(
    { map, onLocationUpdate, updateLoadingState, logMapEvent, isNavigating },
    ref
) {
    const watchId = useRef(null);
    const intervalId = useRef(null);
    const currentMarker = useRef(null);
    const lastUpdate = useRef(Date.now());
    const lastLocationUpdate = useRef(Date.now());
    const updateInterval = useRef(NORMAL_UPDATE_INTERVAL);
    const pendingUpdate = useRef(null);
    const isUpdatingMarker = useRef(false);
    const lastKnownLocation = useRef(null);
    const retryTimeoutId = useRef(null);
    const isMounted = useRef(true);

    const clearCurrentMarker = useCallback(() => {
        if (currentMarker.current && map) {
            map.removeAnnotation(currentMarker.current);
            currentMarker.current = null;
        }
    }, [map]);

    const updateUserLocationMarker = useCallback((location, forceUpdate = false) => {
        // Prevent concurrent updates but allow force updates
        if (isUpdatingMarker.current && !forceUpdate) {
            console.log('📍 [Marker Update Skipped] - Already updating');
            return false;
        }

        const now = Date.now();
        // Only throttle if it's not a force update and not the first update
        // Reduced throttle time from 2000ms to 500ms for more responsive updates
        if (!forceUpdate && lastUpdate.current > 0 && (now - lastUpdate.current < 500)) {
            console.log('📍 [Marker Update Throttled]', {
                timeSinceLastUpdate: now - lastUpdate.current,
                threshold: 500
            });
            return false;
        }

        // Critical check: Ensure map is fully ready before touching marker
        if (!map || !window.mapkit || !map.region) {
            // Don't retry if component is unmounted or map is gone
            if (!isMounted.current || !map) {
                // Clear any existing retry timeout when map is destroyed
                if (retryTimeoutId.current) {
                    clearTimeout(retryTimeoutId.current);
                    retryTimeoutId.current = null;
                }
                return false;
            }

            console.log('⏳ [Map Not Ready] - Retrying in 500ms', {
                hasMap: !!map,
                hasMapkit: !!window.mapkit,
                hasRegion: !!map?.region,
                mapLoading: map?._loading
            });

            // Clear any existing retry timeout
            if (retryTimeoutId.current) {
                clearTimeout(retryTimeoutId.current);
                retryTimeoutId.current = null;
            }

            // Retry after a short delay - but check map existence again
            retryTimeoutId.current = setTimeout(() => {
                // Double-check conditions before retry
                if (isMounted.current && map) {
                    console.log('🔄 [Retrying Marker Creation]');
                    updateUserLocationMarker(location, forceUpdate);
                } else {
                    console.log('🛑 [Retry Cancelled] - Map no longer available');
                }
            }, 500);
            return false;
        }

        isUpdatingMarker.current = true;
        lastUpdate.current = now;

        try {
            const coordinate = new window.mapkit.Coordinate(location.latitude, location.longitude);
            
            console.log('🏗️ [Updating User Marker]', {
                latitude: location.latitude,
                longitude: location.longitude,
                forceUpdate,
                hasCurrentMarker: !!currentMarker.current,
                mapIsReady: !!map.region
            });
            
            // Update existing marker position if possible, otherwise create new one
            if (currentMarker.current && map.annotations?.includes(currentMarker.current)) {
                // Try to update the existing marker's coordinate
                try {
                    currentMarker.current.coordinate = coordinate;
                    currentMarker.current.subtitle = `Accuracy: ${Math.round(location.accuracy || 0)}m`;
                    console.log('✅ [Marker Position Updated]', {
                        latitude: location.latitude.toFixed(6),
                        longitude: location.longitude.toFixed(6)
                    });
                } catch (updateError) {
                    console.log('⚠️ [Cannot update marker position, creating new one]');
                    // If updating fails, create a new marker
                    const newMarker = new window.mapkit.MarkerAnnotation(
                        coordinate,
                        {
                            title: "You are here",
                            subtitle: `Accuracy: ${Math.round(location.accuracy || 0)}m`,
                            color: "#7B6AFF", // magic-lilac color
                            glyphText: "📍",
                            displayPriority: 2000, // Higher than destination markers (900)
                            animates: false, // Disable animation to prevent disappearing
                            selected: false
                        }
                    );
                    
                    // Add new marker first, then remove old
                    map.addAnnotation(newMarker);
                    if (currentMarker.current) {
                        map.removeAnnotation(currentMarker.current);
                    }
                    currentMarker.current = newMarker;
                }
            } else {
                // Create new marker if none exists
                const newMarker = new window.mapkit.MarkerAnnotation(
                    coordinate,
                    {
                        title: "You are here",
                        subtitle: `Accuracy: ${Math.round(location.accuracy || 0)}m`,
                        color: "#7B6AFF", // magic-lilac color
                        glyphText: "📍",
                        displayPriority: 2000, // Higher than destination markers (900)
                        animates: false, // Disable animation to prevent disappearing
                        selected: false
                    }
                );
                
                // Add new marker
                try {
                    map.addAnnotation(newMarker);
                    currentMarker.current = newMarker;
                    
                    console.log('✅ [New Marker Created]', {
                        latitude: location.latitude.toFixed(6),
                        longitude: location.longitude.toFixed(6),
                        totalAnnotations: map.annotations?.length || 0
                    });
                } catch (addError) {
                    console.error('❌ [Marker Add Error]', addError);
                    return false;
                }
            }
            
            // Removed: Automatic map centering to let user control map view manually
            console.log('📍 [Marker Added Without Region Change]', {
                latitude: location.latitude.toFixed(6),
                longitude: location.longitude.toFixed(6),
                userControlledView: true
            });
            
            console.log('✅ [Marker Updated Successfully]', {
                latitude: location.latitude.toFixed(6),
                longitude: location.longitude.toFixed(6),
                accuracy: location.accuracy,
                markerExists: !!currentMarker.current,
                totalAnnotations: map.annotations?.length || 0,
                forceUpdate
            });
            
            return true;
            
        } catch (error) {
            console.error('❌ [Marker Update Error]', error, {
                location,
                hasMap: !!map,
                forceUpdate
            });
            return false;
        } finally {
            isUpdatingMarker.current = false;
        }
    }, [map, isNavigating]);

    const processLocationUpdate = useCallback((location, forceUpdate = false) => {
        const now = Date.now();
        
        // Store the last known location
        lastKnownLocation.current = location;
        
        // Always update marker, with force option
        const markerUpdated = updateUserLocationMarker(location, forceUpdate);
        
        console.log('📱 [Processing Location Update]', {
            location: location,
            markerUpdated,
            forceUpdate,
            isNavigating
        });
        
        // Send location updates for navigation
        if (isNavigating) {
            // During navigation, send updates every 5 seconds
            if (now - lastLocationUpdate.current >= updateInterval.current) {
                console.log('📍 [Sending Navigation Location Update]', {
                    location,
                    timeSinceLastUpdate: now - lastLocationUpdate.current
                });
                lastLocationUpdate.current = now;
                onLocationUpdate(location);
            }
        } else {
            // When not navigating, always send updates (for initial setup)
            console.log('📍 [Sending Initial Location Update]', location);
            onLocationUpdate(location);
        }
    }, [updateUserLocationMarker, onLocationUpdate, isNavigating]);

    const getCurrentLocationForced = useCallback(() => {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        timestamp: position.timestamp
                    };
                    console.log('🔄 [Forced Location Check]', {
                        location,
                        accuracy: position.coords.accuracy,
                        isNavigating
                    });
                    processLocationUpdate(location, true); // Force update for manual requests
                    resolve(location);
                },
                (error) => {
                    console.error('❌ [Forced Location Error]', error);
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 1000 // Allow 1 second old location
                }
            );
        });
    }, [processLocationUpdate, isNavigating]);

    const startLocationTracking = useCallback((highAccuracy = false) => {
        console.log('🚀 [Starting Location Tracking]', { highAccuracy, isNavigating });
        
        // Clear existing watch and interval
        if (watchId.current) {
            navigator.geolocation.clearWatch(watchId.current);
            watchId.current = null;
        }
        
        if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }

        // Start continuous location watching
        watchId.current = navigator.geolocation.watchPosition(
            (position) => {
                const location = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp
                };
                
                console.log('📡 [Watch Position Update]', {
                    location,
                    isNavigating,
                    accuracy: position.coords.accuracy
                });
                
                processLocationUpdate(location, false); // Regular update for watch position
            },
            (error) => {
                console.error('❌ [Watch Position Error]', error);
                logMapEvent('Location Watch Error', error);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 2000 // Allow 2 second old location
            }
        );

        // During navigation, also force location checks every 5 seconds
        if (isNavigating) {
            console.log('⏰ [Starting Periodic Location Checks]');
            intervalId.current = setInterval(() => {
                console.log('⏱️ [Periodic Location Check Triggered]');
                getCurrentLocationForced().catch(error => {
                    console.error('❌ [Periodic Location Check Failed]', error);
                });
            }, LOCATION_CHECK_INTERVAL);
        }

        return () => {
            if (watchId.current) {
                navigator.geolocation.clearWatch(watchId.current);
                watchId.current = null;
            }
            if (intervalId.current) {
                clearInterval(intervalId.current);
                intervalId.current = null;
            }
        };
    }, [processLocationUpdate, getCurrentLocationForced, logMapEvent, isNavigating]);

    const getUserLocation = useCallback(() => {
        updateLoadingState('location', true);
        
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        timestamp: position.timestamp
                    };
                    console.log('🎯 [Initial User Location]', location);
                    
                    // Wait a moment for map to be ready before adding marker
                    if (!map || !map.region) {
                        console.log('⏳ [Waiting for map to be ready for initial location]');
                        setTimeout(() => {
                            processLocationUpdate(location, true); // Force update for initial location
                        }, 1000); // Wait 1 second for map to be ready
                    } else {
                        processLocationUpdate(location, true); // Force update for initial location
                    }
                    
                    updateLoadingState('location', false);
                    resolve(location);
                },
                (error) => {
                    updateLoadingState('location', false);
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });
    }, [processLocationUpdate, updateLoadingState, map]);

    // Effect to handle navigation mode changes
    useEffect(() => {
        // Only start tracking if map is ready
        if (map && map.region) {
            console.log('🔄 [Navigation Mode Changed]', { isNavigating, mapReady: true });
            updateInterval.current = isNavigating ? NAVIGATION_UPDATE_INTERVAL : NORMAL_UPDATE_INTERVAL;
            const cleanup = startLocationTracking(isNavigating);

            return () => {
                cleanup();
                clearCurrentMarker();
                // Clear any pending retry timeout when navigation mode changes
                if (retryTimeoutId.current) {
                    clearTimeout(retryTimeoutId.current);
                    retryTimeoutId.current = null;
                }
            };
        } else {
            console.log('⏳ [Navigation Mode Changed - Map Not Ready]', {
                isNavigating,
                hasMap: !!map,
                hasRegion: !!map?.region
            });
        }
    }, [isNavigating, map, map?.region, startLocationTracking, clearCurrentMarker]);

    // Effect to handle map destruction/navigation away
    useEffect(() => {
        if (!map) {
            console.log('🗺️ [Map Destroyed] - Cleaning up all timers and watchers');

            // Clear retry timeout immediately when map is destroyed
            if (retryTimeoutId.current) {
                clearTimeout(retryTimeoutId.current);
                retryTimeoutId.current = null;
            }

            // Clear location watch
            if (watchId.current) {
                navigator.geolocation.clearWatch(watchId.current);
                watchId.current = null;
            }

            // Clear periodic interval
            if (intervalId.current) {
                clearInterval(intervalId.current);
                intervalId.current = null;
            }
        } else if (map && map.region) {
            console.log('🗺️ [Map Ready] - Will start location tracking on next navigation change');
        }

        // Clean up retry timeout if map is destroyed
        return () => {
            if (retryTimeoutId.current) {
                clearTimeout(retryTimeoutId.current);
                retryTimeoutId.current = null;
            }
        };
    }, [map, map?.region]);

    // Effect to handle route overlay updates and preserve location marker
    useEffect(() => {
        const handleRouteOverlayUpdate = (event) => {
            console.log('📡 [Route Overlay Updated Event Received]', event.detail);
            
            // Small delay to ensure route overlay is fully updated
            setTimeout(() => {
                if (currentMarker.current && map && event.detail.userLocation) {
                    console.log('🔧 [Preserving Location Marker After Route Update]');
                    
                    // Check if marker still exists on map
                    const markerExists = map.annotations?.includes(currentMarker.current);
                    
                    if (!markerExists) {
                        console.log('🚨 [Location Marker Missing] - Re-adding after route update');
                        try {
                            map.addAnnotation(currentMarker.current);
                            console.log('✅ [Location Marker Restored]');
                        } catch (error) {
                            console.error('❌ [Failed to restore location marker]', error);
                            // Force recreate the marker
                            updateUserLocationMarker(event.detail.userLocation, true);
                        }
                    } else {
                        console.log('✅ [Location Marker Still Present]');
                    }
                }
            }, 200); // Small delay to let route update complete
        };
        
        const handleForceLocationUpdate = (event) => {
            console.log('🔄 [Force Location Update Event Received]', event.detail);
            if (event.detail.location && map) {
                // Force update the location marker
                updateUserLocationMarker(event.detail.location, true);
            }
        };

        window.addEventListener('routeOverlayUpdated', handleRouteOverlayUpdate);
        window.addEventListener('forceLocationUpdate', handleForceLocationUpdate);
        
        return () => {
            window.removeEventListener('routeOverlayUpdated', handleRouteOverlayUpdate);
            window.removeEventListener('forceLocationUpdate', handleForceLocationUpdate);
        };
    }, [map, updateUserLocationMarker]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        // Set mounted flag on mount
        isMounted.current = true;

        return () => {
            // Set mounted flag to false to prevent any async operations
            isMounted.current = false;

            // Clear retry timeout
            if (retryTimeoutId.current) {
                clearTimeout(retryTimeoutId.current);
                retryTimeoutId.current = null;
            }

            // Clear location watch
            if (watchId.current) {
                navigator.geolocation.clearWatch(watchId.current);
                watchId.current = null;
            }

            // Clear periodic interval
            if (intervalId.current) {
                clearInterval(intervalId.current);
                intervalId.current = null;
            }

            // Clear marker
            clearCurrentMarker();
        };
    }, [clearCurrentMarker]);

    useImperativeHandle(ref, () => ({
        getUserLocation,
        getCurrentLocationForced,
        getLastKnownLocation: () => lastKnownLocation.current
    }), [getUserLocation, getCurrentLocationForced]);

    return null;
});

LocationManager.displayName = 'LocationManager';

export default LocationManager; 